import axios from "axios";
import { getToken } from "../utils/authStorage";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5010/api";

/**
 * Combined Income Report Service
 * Follow-up style service with dynamic filters merging regular + hidden income
 */

// Interfaces
export interface FilterCondition {
  field: string;
  operator: "=" | "!=" | ">" | ">=" | "<" | "<=" | "between" | "last30Days" | "last90Days" | "lastMonth" | "last3Months" | "last6Months" | "lastYear";
  value: string | number | boolean | [number, number] | [string, string];
}

export interface CombinedFilters {
  logic?: "AND" | "OR";
  conditions?: FilterCondition[];
}

export interface CombinedBooking {
  bookingId: string;
  bookingDate: string;
  customerName: string;
  customerContact: string;
  projectName: string;
  unitRef: string;
  unitType: string;
  wing: string;
  paymentTerms: string;
  regularExpected: number;
  regularReceived: number;
  regularPending: number;
  regularPaymentCount: number;
  hiddenExpected: number;
  hiddenCollected: number;
  hiddenPending: number;
  hiddenCollectionCount: number;
  totalExpected: number;
  totalReceived: number;
  totalPending: number;
  pendingPercent: number;
  receivedPercent: number;
  totalPaymentCount: number;
  lastPaymentDate: string | null;
  lastPaymentAmount: number | null;
  daysSinceBooking: number;
  daysSinceLastPayment: number | null;
  status: string;
  // Period-specific amounts (when payment date filter is active)
  saleDeedReceivedInPeriod?: number;
  receivableCollectedInPeriod?: number;
  totalReceivedInPeriod?: number;
}

export interface Project {
  id: number;
  name: string;
}

export interface CombinedTotals {
  regularExpected: number;
  regularReceived: number;
  regularPending: number;
  hiddenExpected: number;
  hiddenCollected: number;
  hiddenPending: number;
  totalExpected: number;
  totalReceived: number;
  totalPending: number;
  pendingPercent: number;
  receivedPercent: number;
  totalPaymentCount: number;
  // Period totals (when payment date filter is active)
  saleDeedReceivedInPeriod?: number;
  receivableCollectedInPeriod?: number;
  totalReceivedInPeriod?: number;
}

export interface CombinedReportData {
  bookings: CombinedBooking[];
  projects: Project[];
  totals: CombinedTotals;
  periodActive?: boolean;
  periodLabel?: string;
}

class CombinedReportService {
  /**
   * Get combined report data with optional dynamic filters
   */
  async getCombinedReportData(
    projectId?: string,
    dateFrom?: string,
    dateTo?: string,
    filters?: CombinedFilters
  ): Promise<CombinedReportData> {
    const token = getToken();
    const payload: any = {};

    if (projectId) payload.projectId = projectId;
    if (dateFrom) payload.dateFrom = dateFrom;
    if (dateTo) payload.dateTo = dateTo;
    if (filters) payload.filters = filters;

    const response = await axios.post(`${API_URL}/reports/combined/data`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  }
}

export default new CombinedReportService();
